import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import ComponentSidebar from '../components/sidebar/ComponentSidebar';
import PropertiesPanel from '../components/properties/PropertiesPanel';
import DesignerCanvas from '../components/canvas/DesignerCanvas';
import DesignerHeader from './header/DesignerHeader';
import {
  CreateFormModal,
  CreateFormModalSubmitData,
} from '../../../../../components/CreateFormModal';
import { TextField } from 'ui/components/TextField/TextField';

import {
  LabelElement,
  ElementType,
  PageSize,
  isGroupElement,
  GroupElement,
  AdvancedTableData,
  AdvancedTableHeader,
  AdvancedTableCellData,
  BaseElementProps,
  DynamicDataField,
  TemplateApiPayload,
} from '../types';
import { useHistory } from '../hooks/useHistory';
import jsPDF from 'jspdf';
import { Stage as KonvaStage } from 'konva/lib/Stage';
import { v4 as uuid } from 'uuid';
import SalesOrderSelectSidebar from './sidebar/SalesOrderSelectSidebar';
import { isEqual } from 'lodash';
import { SalesOrder } from 'services/salesOrders';
import { fetchCustomFieldsAPI, ObjectType } from 'services/customFields';
import { SALES_ORDER_FIELDS } from './properties/consts';
import {
  createTemplateApi,
  getTemplatesApi,
  updateTemplateApi,
  deleteTemplateApi,
  getTemplateByIdApi,
} from 'services/templates/api';

import { showNotification } from 'services/api'; // eslint-disable-line
import { logErrorCtx } from 'app/logging';

interface LabelDesignerProps {
  reportId?: string; // Assuming optional, adjust if necessary
}

const PAGE_SIZES: PageSize[] = [
  { name: 'Letter (8.5" x 11")', width: 816, height: 1056 },
];

const LabelDesigner = ({
  reportId = 'sales order', // Default reportId if not provided
}: LabelDesignerProps): React.ReactElement => {
  const [selectedElement, setSelectedElement] = useState<LabelElement | null>(
    null
  );
  const [selectedElements, setSelectedElements] = useState<LabelElement[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedPageSize, setSelectedPageSize] = useState<PageSize>(
    PAGE_SIZES[0]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('template-export');
  const [selectedSalesOrder, setSelectedSalesOrder] =
    useState<SalesOrder | null>(null);
  const [dynamicCustomFields, setDynamicCustomFields] = useState<
    DynamicDataField[]
  >([]);

  const [availableTemplates, setAvailableTemplates] = useState<
    TemplateApiPayload[]
  >([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = React.useState(false);

  const [isCreateFormModalOpen, setIsCreateFormModalOpen] =
    React.useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(
    null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDeleteId, setTemplateToDeleteId] = useState<string | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

  const handleOpenCreateFormModal = () => setIsCreateFormModalOpen(true);
  const handleCloseCreateFormModal = () => setIsCreateFormModalOpen(false);

  const handleUpdateTemplate = async () => {
    setIsUpdatingTemplate(true);
    if (!currentTemplateId) {
      showNotification('No template selected to update.', {
        variant: 'warning',
      });
      setIsUpdatingTemplate(false); // Reset on early return
      return;
    }

    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );
    if (!currentTemplate) {
      showNotification('Selected template not found.', { variant: 'error' });
      setIsUpdatingTemplate(false); // Reset on early return
      return;
    }

    const payload: TemplateApiPayload = {
      ...currentTemplate, // Spreads id, name, formType, defaultTemplate
      template: {
        elements: elements, // Current elements from useHistory
        pageSize: selectedPageSize,
        version: currentTemplate.template.version, // Or update version logic if needed
      },
    };

    try {
      const updatedTemplateResponse = await updateTemplateApi(payload);
      setAvailableTemplates((prevTemplates) =>
        prevTemplates.map((t) =>
          t.id === updatedTemplateResponse.id ? updatedTemplateResponse : t
        )
      );
      showNotification('Template updated successfully!', {
        variant: 'success',
      });
    } catch (error) {
      if (error instanceof Error) {
        logErrorCtx('Failed to update template', { error });
      } else {
        logErrorCtx('Failed to update template', {
          error: new Error(String(error)),
        });
      }
      showNotification('Failed to update template.', { variant: 'error' });
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  const handleCreateFormSubmit = async (data: CreateFormModalSubmitData) => {
    setIsTemplatesLoading(true);
    try {
      let templateToCreate: TemplateApiPayload;

      if (data.startingPoint === 'template' && data.templateId) {
        const baseTemplate = availableTemplates.find(
          (t) => t.id === data.templateId
        );

        if (baseTemplate) {
          templateToCreate = {
            id: '',
            name: data.templateName,
            defaultTemplate: false,
            formType: baseTemplate.formType,
            template: baseTemplate.template,
          };
        } else {
          throw new Error('Base template not found');
        }
      } else {
        // Creating from scratch
        templateToCreate = {
          id: '',
          name: data.templateName,
          defaultTemplate: false,
          formType: reportId,
          template: {
            elements: [],
            pageSize: selectedPageSize,
            version: '1.0',
          },
        };
      }

      const newTemplate = await createTemplateApi([templateToCreate]);

      handleCloseCreateFormModal(); // Close modal ONLY on success
      showNotification('Template created successfully!', {
        variant: 'success',
      });

      // Refetch templates to include the new one and then select it
      await fetchTemplates(reportId, newTemplate.id);
    } catch (error: any) {
      let errorMessage =
        'An unexpected error occurred while creating the template.';

      // Axios-specific error structure for detailed feedback
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      logErrorCtx('Failed to create template', { error });
      showNotification(errorMessage, { variant: 'error' });
    } finally {
      setIsTemplatesLoading(false); // This will stop the button spinner
    }
  };

  const { elements, recordChange, undo, redo, canUndo, canRedo } = useHistory(
    []
  );

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);
  const stageRef = useRef<KonvaStage>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomFields = async () => {
      try {
        const customFields = (
          await fetchCustomFieldsAPI({}, ObjectType.SalesOrder)
        ).data;

        if (customFields && Array.isArray(customFields)) {
          const salesOrderCustomFields = customFields.map((field: any) => ({
            value: `customFields.${field.name}`, // e.g., customFields.Date
            label: field.name, // e.g., Date
            group: 'Custom Fields',
          }));
          if (isMounted) {
            setDynamicCustomFields(salesOrderCustomFields);
          }
        }
      } catch (error) {
        console.error('Failed to fetch custom fields:', error);
        // Handle error appropriately, e.g., show a notification to the user
      }
    };

    fetchCustomFields();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array ensures this runs once on mount

  const fetchTemplates = async (
    formType: string,
    selectTemplateId?: string
  ) => {
    setIsTemplatesLoading(true);
    try {
      const templates = await getTemplatesApi(formType);
      const verifiedTemplates = templates.filter(
        (template) =>
          template.template &&
          template.template.elements &&
          template.template.pageSize &&
          template.template.version
      );
      setAvailableTemplates(verifiedTemplates);

      let templateToSelect = null;
      if (selectTemplateId) {
        templateToSelect = verifiedTemplates.find(
          (t) => t.id === selectTemplateId
        );
      } else if (!currentTemplateId && verifiedTemplates.length > 0) {
        // Select first template if none is selected
        templateToSelect = verifiedTemplates[0];
      }

      if (templateToSelect) {
        handleTemplateSelection(templateToSelect);
      }
    } catch (error) {
      if (error instanceof Error) {
        logErrorCtx('Failed to fetch templates', { error });
      } else {
        logErrorCtx('Failed to fetch templates', {
          error: new Error(String(error)),
        });
      }
      showNotification('Failed to load templates.', { variant: 'error' });
      setAvailableTemplates([]);
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const allAvailableDynamicFields = useMemo(() => {
    // Combine static fields with dynamically fetched custom fields
    return [...SALES_ORDER_FIELDS, ...dynamicCustomFields];
  }, [dynamicCustomFields, SALES_ORDER_FIELDS]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 'z') {
          if (e.shiftKey) {
            if (canRedo) redo();
          } else {
            if (canUndo) undo();
          }
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  // Add this new useEffect to re-sync selectedElement after elements array changes (e.g., from undo/redo)
  useEffect(() => {
    if (selectedElement) {
      const refreshedSelectedElement = elements.find(
        (el) => el.id === selectedElement.id
      );
      if (refreshedSelectedElement) {
        if (!isEqual(selectedElement, refreshedSelectedElement)) {
          setSelectedElement(refreshedSelectedElement);
        }
      } else {
        setSelectedElement(null);
      }
    }
  }, [elements, selectedElement]); // Intentionally include selectedElement in deps to handle its changes too

  // Calculate total pages based on elements' positions
  useEffect(() => {
    const maxY = elements.reduce((max, element) => {
      return Math.max(max, element.y + element.height);
    }, 0);

    const calculatedPages = Math.max(
      1,
      Math.ceil(maxY / selectedPageSize.height)
    );
    setTotalPages(calculatedPages);

    if (currentPage > calculatedPages) {
      setCurrentPage(calculatedPages);
    }
  }, [elements, selectedPageSize.height, currentPage]);

  // Handle keyboard events for deletion
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !previewMode &&
        selectedElement &&
        (e.key === 'Delete' || e.key === 'Backspace')
      ) {
        const newElements = elements.filter(
          (element) => element.id !== selectedElement.id
        );
        recordChange(newElements);
        setSelectedElement(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, elements, recordChange, previewMode]);

  const handleBringToFront = () => {
    if (!selectedElement) return;

    const newElements = elements.filter((el) => el.id !== selectedElement.id);
    const updatedElements = [...newElements, selectedElement];
    recordChange(updatedElements);
  };

  const handleSendToBack = () => {
    if (!selectedElement) return;

    const newElements = elements.filter((el) => el.id !== selectedElement.id);
    const updatedElements = [selectedElement, ...newElements];
    recordChange(updatedElements);
  };

  const handleImageUpload = (x: number, y: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newElement: LabelElement = {
            id: uuid(),
            type: 'image',
            x,
            y,
            width: 100,
            height: 100,
            rotation: 0,
            src: e.target?.result as string,
          };

          const newElements = [...elements, newElement];
          recordChange(newElements);
          setSelectedElement(newElement);
        };
        reader.readAsDataURL(file);
      }
    };

    input.click();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && over.id === 'canvas') {
      const type = active.id as ElementType;
      const canvasRect = document
        .getElementById('label-canvas')
        ?.getBoundingClientRect();
      const stageRect = stageRef.current?.container()?.getBoundingClientRect();

      if (canvasRect && stageRect && stageRef.current) {
        const scale = stageRef.current.scaleX();
        const stage = stageRef.current;

        const pointerPosition = stage.getPointerPosition();
        if (pointerPosition) {
          // Get canvas dimensions
          const canvasWidth = selectedPageSize.width;
          const canvasHeight = selectedPageSize.height * totalPages;

          // Calculate element dimensions dynamically based on type
          const elementWidth =
            type === 'advancedTable'
              ? canvasWidth * 0.9 // Initial width 90% of canvas
              : type === 'line'
              ? 100
              : 100;
          const elementHeight =
            type === 'advancedTable'
              ? 150 // Keep height or adjust as needed, e.g., relative to rows
              : type === 'line'
              ? 10
              : 50;

          // Determine the drop position (assuming x, y from onDrop are relative to the drop target or stage)
          const dropX = pointerPosition.x / scale;
          const dropY = pointerPosition.y / scale;

          // Constrain coordinates to keep the element center within the canvas bounds
          // and then calculate top-left based on that center.
          const constrainedCenterX = Math.max(
            elementWidth / 2, // Use dynamic elementWidth
            Math.min(dropX, canvasWidth - elementWidth / 2) // Use dynamic elementWidth
          );
          const constrainedCenterY = Math.max(
            elementHeight / 2, // Use dynamic elementHeight
            Math.min(dropY, canvasHeight - elementHeight / 2) // Use dynamic elementHeight
          );

          const finalX = constrainedCenterX - elementWidth / 2; // Use dynamic elementWidth
          const finalY = constrainedCenterY - elementHeight / 2; // Use dynamic elementHeight

          let elementSpecificProps: Partial<
            Omit<LabelElement, keyof BaseElementProps>
          > = {};
          let advancedTableCalculatedHeight: number | undefined; // To store specific height for advanced table

          if (type === 'image') {
            handleImageUpload(finalX, finalY);
            return;
          }

          if (type === 'text') {
            elementSpecificProps = {
              text: 'Sample Text',
              fontSize: 16,
              fontFamily: 'Arial',
              fill: '#000000',
              align: 'left',
              backgroundColor: 'transparent',
            };
          } else if (type === 'box') {
            elementSpecificProps = {
              fill: 'transparent',
              stroke: '#000000',
              lineWidth: 1,
            };
          } else if (type === 'line') {
            elementSpecificProps = {
              stroke: '#000000',
              lineWidth: 1,
            };
          } else if (type === 'advancedTable') {
            // Constants for height calculation
            const initialHeaderRowHeight = 30;
            const initialDefaultCellFontSize = 13;
            const initialDefaultNumDataRows = 1;
            const DATA_ROW_PADDING = 8; // Set padding to 0
            const defaultDataRowHeight =
              initialDefaultCellFontSize + DATA_ROW_PADDING;

            // Recalculate elementHeight specifically for advancedTable based on new row height logic
            // The elementHeight variable was previously set based on a generic value for advancedTable.
            // We override it here.
            const newElementHeight =
              initialHeaderRowHeight +
              initialDefaultNumDataRows * defaultDataRowHeight;
            // Update the general elementHeight that will be used for the newElement creation
            // This is a bit tricky as elementHeight is declared outside this block. For clarity,
            // we'll use newElementHeight for calculations within this block and ensure the final
            // newElement uses this adjusted height.

            const defaultHeaders: AdvancedTableHeader[] = [
              { key: 'name', label: 'Name' },
              { key: 'description', label: 'Description' },
              { key: 'salesOrderItemType', label: 'Type' },
              { key: 'quantity', label: 'Qty' },
              { key: 'price', label: 'Price' },
              { key: 'discount', label: 'Discount' },
              { key: 'total', label: 'Total' },
            ];

            const defaultNumColumns = defaultHeaders.length;

            const defaultColumnWidths = Array(defaultNumColumns).fill(
              elementWidth / defaultNumColumns // elementWidth is from the outer scope
            );
            // Use the calculated defaultDataRowHeight for each data row
            const defaultRowHeights = Array(initialDefaultNumDataRows).fill(
              defaultDataRowHeight
            );

            const defaultTableData: AdvancedTableCellData[][] = Array(
              initialDefaultNumDataRows
            )
              .fill(null)
              .map(() =>
                Array(defaultNumColumns)
                  .fill(null)
                  .map(() => ({ content: `` }))
              );

            const advancedTableSpecificProps: AdvancedTableData = {
              numRows: initialDefaultNumDataRows,
              numColumns: defaultNumColumns,
              columnWidths: defaultColumnWidths,
              rowHeights: defaultRowHeights, // Use the new defaultRowHeights
              data: defaultTableData,
              headerRow: true,
              headerRowHeight: initialHeaderRowHeight,
              headers: defaultHeaders,
              headerFontFamily: 'Arial',
              headerFontSize: 15,
              headerBackgroundColor: '#f0f0f0',
              headerTextColor: '#000000',
              defaultCellFontFamily: 'Arial',
              defaultCellFontSize: initialDefaultCellFontSize,
              defaultCellBackgroundColor: '#ffffff',
              defaultCellTextColor: '#000000',
              borderColor: '#cccccc',
              borderWidth: 1,
              showGrid: true,
              highlightColor: '#007bff',
            };
            elementSpecificProps = {
              advancedTableData: advancedTableSpecificProps,
            };
            // Ensure the newElement uses the recalculated height for advancedTable
            // This is a direct assignment to the variable that will be used later.
            // This assumes elementHeight was declared with 'let' in the outer scope.
            // If it was 'const', this approach needs rethinking, but based on typical patterns, it's 'let'.
            // To be safer, we'll ensure the newElement below uses this specific height.
            advancedTableCalculatedHeight = newElementHeight; // Store the calculated height
          }

          const newElement: LabelElement = {
            id: uuid(),
            type,
            x: finalX,
            y: finalY,
            width: elementWidth,
            height:
              type === 'advancedTable' &&
              advancedTableCalculatedHeight !== undefined
                ? advancedTableCalculatedHeight
                : elementHeight,
            rotation: 0,
            draggable: true,
            ...elementSpecificProps,
          };

          const newElements = [...elements, newElement];
          recordChange(newElements);
          setSelectedElement(newElement);
        }
      }
    }

    setIsDragging(false);
  };

  const handleExportPDF = async () => {
    setPdfFilename('template-export');
    setPdfExportDialogOpen(true);
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleExportPDFConfirm = async () => {
    setIsExporting(true);
    if (!stageRef.current) {
      setIsExporting(false);
      return;
    }
    const stage = stageRef.current;

    // Store original state
    const gridVisible = showGrid;
    const originalScale = stage.scale();
    const originalPosition = stage.position();

    // Prepare for export
    if (gridVisible) setShowGrid(false);
    setSelectedElement(null);
    setSelectedElements([]);

    // Allow Konva to re-render before exporting
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: [selectedPageSize.width, selectedPageSize.height],
      });

      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.draw();

      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        const dataUrl = stage.toDataURL({
          x: 0,
          y: i * selectedPageSize.height,
          width: selectedPageSize.width,
          height: selectedPageSize.height,
          pixelRatio: 2, // Increase for better quality
        });
        pdf.addImage(
          dataUrl,
          'PNG',
          0,
          0,
          selectedPageSize.width,
          selectedPageSize.height
        );
      }

      pdf.save(`${pdfFilename}.pdf`);
    } catch (error) {
      logErrorCtx('Error exporting PDF', { error: error as Error });
      showNotification('Failed to export PDF. Please try again.', {
        variant: 'error',
      });
    } finally {
      // Restore original stage state
      stage.scale(originalScale);
      stage.position(originalPosition);
      stage.draw();

      if (gridVisible) setShowGrid(true);

      setPdfExportDialogOpen(false);
      setIsExporting(false);
    }
  };

  const handleTogglePreview = () => {
    setPreviewMode(!previewMode);
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleToggleGrid = () => {
    setShowGrid(!showGrid);
  };

  const handleUpdateElement = (
    id: string,
    updates: Partial<LabelElement>,
    updateType?: string
  ) => {
    const newElements = elements.map((el) =>
      el.id === id ? { ...el, ...updates } : el
    );
    recordChange(newElements);
    setSelectedElement((prevSelected) =>
      prevSelected && prevSelected.id === id
        ? { ...prevSelected, ...updates }
        : prevSelected
    );

    if (
      selectedElement &&
      selectedElement.type === 'advancedTable' &&
      (updateType === 'width' || updateType === 'height')
    ) {
      const tableElement = selectedElement as any;
      const newTableData = { ...tableElement.advancedTableData };
      if (updateType === 'width' && updates.width !== undefined) {
        newTableData.columnWidths = newTableData.columnWidths.map(
          () => (updates.width as number) / newTableData.numColumns
        );
      }
      if (updateType === 'height' && updates.height !== undefined) {
        newTableData.rowHeights = newTableData.rowHeights.map(
          () => (updates.height as number) / newTableData.numRows
        );
      }
      const newElement = {
        ...selectedElement,
        advancedTableData: newTableData,
      };
      const newElements = elements.map((el) =>
        el.id === id ? newElement : el
      );
      recordChange(newElements);
      setSelectedElement(newElement);
    }
  };

  const handleDuplicateElement = (id: string): LabelElement | undefined => {
    const elementToDuplicate = elements.find((element) => element.id === id);

    if (elementToDuplicate) {
      if (isGroupElement(elementToDuplicate)) {
        const duplicatedGroup: GroupElement = {
          ...elementToDuplicate,
          id: uuid(),
          x: elementToDuplicate.x + 20,
          y: elementToDuplicate.y + 20,
          children: elementToDuplicate.children.map((child) => ({
            ...child,
            id: uuid(),
          })),
        };
        const newElements = [...elements, duplicatedGroup];
        recordChange(newElements);
        return duplicatedGroup;
      }
      const duplicatedElement: LabelElement = {
        ...elementToDuplicate,
        id: uuid(),
        x: elementToDuplicate.x + 20,
        y: elementToDuplicate.y + 20,
      };

      const newElements = [...elements, duplicatedElement];
      recordChange(newElements);
      return duplicatedElement;
    }

    return undefined;
  };

  const handleClearCanvas = () => {
    recordChange([]);
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleGroup = () => {
    if (selectedElements.length > 1) {
      let allElements: LabelElement[] = [];

      selectedElements.forEach((element) => {
        if (isGroupElement(element)) {
          const groupChildren = element.children.map((child) => ({
            ...child,
            x: child.x + element.x,
            y: child.y + element.y,
          }));
          allElements = [...allElements, ...groupChildren];
        } else {
          allElements.push(element);
        }
      });

      const minX = Math.min(...allElements.map((el) => el.x));
      const minY = Math.min(...allElements.map((el) => el.y));
      const maxX = Math.max(...allElements.map((el) => el.x + el.width));
      const maxY = Math.max(...allElements.map((el) => el.y + el.height));
      const width = maxX - minX;
      const height = maxY - minY;

      const adjustedElements = allElements.map((el) => ({
        ...el,
        id: uuid(),
        x: el.x - minX,
        y: el.y - minY,
      }));

      const groupElement: GroupElement = {
        id: uuid(),
        type: 'group',
        children: adjustedElements,
        x: minX,
        y: minY,
        width,
        height,
        rotation: 0,
      };

      const selectedIds = new Set(selectedElements.map((el) => el.id));
      const newElements = elements.filter((el) => !selectedIds.has(el.id));

      recordChange([...newElements, groupElement]);
      setSelectedElement(groupElement);
      setSelectedElements([]);
    }
  };

  const handleUngroup = () => {
    if (selectedElement && isGroupElement(selectedElement)) {
      const currentGroup = elements.find(
        (element) => element.id === selectedElement.id
      ) as GroupElement | undefined;

      if (!currentGroup) return;

      const newElements = elements.filter(
        (element) => element.id !== currentGroup.id
      );

      const restoredChildren = currentGroup.children.map((child) => {
        const groupRotationRad = ((currentGroup.rotation || 0) * Math.PI) / 180;
        const cosAngle = Math.cos(groupRotationRad);
        const sinAngle = Math.sin(groupRotationRad);

        const relativeX = child.x;
        const relativeY = child.y;
        const rotatedX = relativeX * cosAngle - relativeY * sinAngle;
        const rotatedY = relativeX * sinAngle + relativeY * cosAngle;

        return {
          ...child,
          id: uuid(),
          x: rotatedX + currentGroup.x,
          y: rotatedY + currentGroup.y,
          rotation: (child.rotation || 0) + (currentGroup.rotation || 0),
        };
      });

      recordChange([...newElements, ...restoredChildren]);
      setSelectedElement(null);
      setSelectedElements([]);
    }
  };

  const handleTemplateSelection = (
    selectedTemplate: TemplateApiPayload | null
  ) => {
    if (selectedTemplate) {
      recordChange(selectedTemplate.template.elements);
      setSelectedPageSize(selectedTemplate.template.pageSize);
      setCurrentTemplateId(selectedTemplate.id);
      setSelectedElement(null);
      setSelectedElements([]);
    } else {
      // Clear the designer if null is selected (e.g., load a blank template)
      recordChange([]); // Clear elements
      setSelectedPageSize(PAGE_SIZES[0]); // Reset to default page size
      setCurrentTemplateId(null);
      setSelectedElement(null);
      setSelectedElements([]);
    }
  };

  const handleDeleteTemplateInitiate = (templateId: string) => {
    setTemplateToDeleteId(templateId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTemplateConfirm = async () => {
    setIsDeleting(true);
    if (!templateToDeleteId) return;

    try {
      await deleteTemplateApi(templateToDeleteId);
      showNotification('Template deleted successfully.', {
        variant: 'success',
      });

      const newAvailableTemplates = availableTemplates.filter(
        (t) => t.id !== templateToDeleteId
      );
      setAvailableTemplates(newAvailableTemplates);

      if (
        templateToDeleteId === currentTemplateId ||
        (currentTemplateId && newAvailableTemplates.length === 0)
      ) {
        let nextTemplateToSelect: TemplateApiPayload | null = null;
        if (newAvailableTemplates.length > 0) {
          nextTemplateToSelect =
            newAvailableTemplates.find((t) => t.defaultTemplate) || // Prefer default
            newAvailableTemplates[0]; // Fallback to first
        }
        handleTemplateSelection(nextTemplateToSelect); // This will set currentTemplateId and load data
      }
      // Optionally, call fetchTemplates(reportId) again if server-side filtering/pagination is complex
      // For now, optimistic update of availableTemplates is fine.
    } catch (error) {
      logErrorCtx(
        `Failed to delete template in LabelDesigner. Template ID: ${templateToDeleteId}`,
        { error: error instanceof Error ? error : new Error(String(error)) }
      );
      showNotification('Failed to delete template.', { variant: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setTemplateToDeleteId(null);
      setIsDeleting(false);
    }
  };

  const handleSetAsDefault = async (templateId: string) => {
    try {
      // 1. Fetch the full template
      const currentTemplate = await getTemplateByIdApi(templateId);

      // 2. Create the updated payload, ensuring all fields are present
      const updatedTemplatePayload: TemplateApiPayload = {
        ...currentTemplate,
        defaultTemplate: true,
      };

      // 3. Call the existing updateTemplateApi
      await updateTemplateApi(updatedTemplatePayload);

      showNotification('Template set as default.', { variant: 'success' });
      // Re-fetch templates to update the default status across all templates
      // and ensure the local state reflects the change made by potentially another user.
      fetchTemplates(reportId);
    } catch (error) {
      logErrorCtx(
        `Failed to set template as default. Template ID: ${templateId}`,
        { error: error instanceof Error ? error : new Error(String(error)) }
      );
      showNotification('Failed to set template as default.', {
        variant: 'error',
      });
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <DesignerHeader
          onTogglePreview={handleTogglePreview}
          previewMode={previewMode}
          showGrid={showGrid}
          onToggleGrid={handleToggleGrid}
          selectedPageSize={selectedPageSize}
          onPageSizeChange={setSelectedPageSize}
          pageSizes={PAGE_SIZES}
          onUndo={canUndo ? undo : undefined}
          onRedo={canRedo ? redo : undefined}
          onClearCanvas={handleClearCanvas}
          onGroup={handleGroup}
          selectedElements={selectedElements}
          availableTemplates={availableTemplates}
          onTemplateSelect={handleTemplateSelection}
          currentTemplateId={currentTemplateId}
          onCreateNewTemplate={handleOpenCreateFormModal}
          onUpdateTemplate={handleUpdateTemplate}
          isUpdatingTemplate={isUpdatingTemplate}
          onDeleteTemplate={handleDeleteTemplateInitiate}
          onSetAsDefault={handleSetAsDefault}
          isCurrentTemplateDefault={
            availableTemplates.find((t) => t.id === currentTemplateId)
              ?.defaultTemplate || false
          }
        />

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {previewMode ? (
            <SalesOrderSelectSidebar
              selectedSalesOrder={selectedSalesOrder}
              setSelectedSalesOrder={setSelectedSalesOrder}
              onExportPDF={handleExportPDF}
            />
          ) : (
            <ComponentSidebar />
          )}

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              bgcolor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <DesignerCanvas
              stageRef={stageRef}
              elements={elements}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              selectedElements={selectedElements}
              setSelectedElements={setSelectedElements}
              onUpdateElement={handleUpdateElement}
              isDragging={isDragging}
              previewMode={previewMode}
              previewData={selectedSalesOrder}
              showGrid={showGrid}
              canvasWidth={selectedPageSize.width}
              canvasHeight={selectedPageSize.height * totalPages}
              currentPage={currentPage}
            />
          </Box>

          {!previewMode && (
            <PropertiesPanel
              selectedElement={selectedElement}
              canvasWidth={selectedPageSize.width}
              onUpdateElement={handleUpdateElement}
              onDeleteElement={() => {
                if (selectedElement) {
                  const newElements = elements.filter(
                    (element) => element.id !== selectedElement.id
                  );
                  recordChange(newElements);
                  setSelectedElement(null);
                }
              }}
              onDuplicateElement={
                selectedElement
                  ? () => {
                      const newElement = handleDuplicateElement(
                        selectedElement.id
                      );
                      if (newElement) {
                        setSelectedElement(newElement);
                      }
                    }
                  : undefined
              }
              onBringToFront={handleBringToFront}
              onSendToBack={handleSendToBack}
              onUngroup={handleUngroup}
              availableFields={allAvailableDynamicFields}
            />
          )}
        </Box>
      </DndContext>
      {/* PDF Export Dialog */}
      <ConfirmationModal
        open={pdfExportDialogOpen}
        title="Export PDF"
        body={
          <Box sx={{ mt: 2, mb: -2, width: '100%' }}>
            <TextField
              fullWidth
              name="pdf-filename"
              label="PDF Filename"
              value={pdfFilename}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[\\/:*?"<>|]/g, '');
                setPdfFilename(sanitized);
              }}
              placeholder="Enter filename"
              variant="standard"
              className="redesign"
              InputProps={{
                endAdornment: '.pdf',
              }}
              autoFocus
              data-qa="pdf-filename-input"
            />
          </Box>
        }
        confirmLabel="Export"
        cancelLabel="Cancel"
        onConfirmClicked={handleExportPDFConfirm}
        onCancelClicked={() => setPdfExportDialogOpen(false)}
        isLoading={isExporting}
        dataQa="export-pdf-dialog"
      />
      <CreateFormModal
        open={isCreateFormModalOpen}
        templates={availableTemplates.map((t) => ({
          id: t.id,
          name: t.name,
        }))}
        isLoadingTemplates={isTemplatesLoading}
        onClose={handleCloseCreateFormModal}
        onSubmit={handleCreateFormSubmit}
        defaultTemplateName="New Label Template"
      />
      <ConfirmationModal
        open={deleteConfirmOpen}
        title="Delete Template"
        body={`Are you sure you want to delete this template? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirmClicked={handleDeleteTemplateConfirm}
        onCancelClicked={() => {
          setDeleteConfirmOpen(false);
          setTemplateToDeleteId(null);
          setIsDeleting(false);
        }}
        dataQa="delete-template-confirmation-modal"
        isLoading={isDeleting}
        confirmButtonRed={true}
      />
    </Box>
  );
};

export default LabelDesigner;
